import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';

export type HistoryTab = 'recent-activity' | 'viewed-me';
export const historyTabAtom = atom<HistoryTab>('recent-activity');

import { userDataAtom } from 'src/stores/auth/atoms';
import { getAtomWithStorage, getUserScopedStorageKey } from 'src/utils/localStorageUtils';
import { MINUTE_MS } from 'src/utils/time';

// 7초 이상은 history에 표시됨
const HISTORY_LOG_MATCH_DURATION_MS = 1000 * 7;
// 120초 이상은 quality 매치임
const QUALITY_MATCH_DURATION_MS = 2 * MINUTE_MS;

const historyNotificationViewCountAtomFamily = atomFamily((userId: string) =>
  getAtomWithStorage<number>(getUserScopedStorageKey(`historyNotificationViewCount`, userId), 0)
);

type HistoryNotification = 'match_7s' | 'match_120s' | null;
const historyNotificationAtomFamily = atomFamily((userId: string) =>
  getAtomWithStorage<HistoryNotification>(
    getUserScopedStorageKey(`historyNotification`, userId),
    null
  )
);

export const historyNotificationAtom = atom((get) => {
  const userData = get(userDataAtom);
  if (!userData) return false;
  return get(historyNotificationAtomFamily(userData.userId));
});

export const markHistoryNotificationAsReadAtom = atom(null, (get, set) => {
  const userData = get(userDataAtom);
  if (!userData) return;

  set(historyNotificationAtomFamily(userData.userId), null);
  set(historyNotificationViewCountAtomFamily(userData.userId), (prev) => prev + 1);
});

const lastVisitedRecentActivityAtomFamily = atomFamily((userId: string) =>
  getAtomWithStorage<number>(getUserScopedStorageKey('lastVisitedRecentActivity', userId), 0)
);

export const lastVisitedRecentActivityAtom = atom((get) => {
  const userData = get(userDataAtom);
  if (!userData) return 0;
  return get(lastVisitedRecentActivityAtomFamily(userData.userId));
});

export const markRecentActivityVisitedAtom = atom(null, (get, set) => {
  const userData = get(userDataAtom);
  if (!userData) return;
  set(lastVisitedRecentActivityAtomFamily(userData.userId), Date.now());
});

export const updateHistoryNotificationByMatchAtom = atom(
  null,
  (get, set, { matchDurationMs }: { matchDurationMs: number }) => {
    const userData = get(userDataAtom);
    if (!userData) return;

    const historyNotification = get(historyNotificationAtomFamily(userData.userId));
    if (historyNotification) {
      return;
    }

    const viewCount = get(historyNotificationViewCountAtomFamily(userData.userId));

    // 최초 1회는 red dot 노출 정책 = History 매치 이상
    if (viewCount === 0 && matchDurationMs >= HISTORY_LOG_MATCH_DURATION_MS) {
      set(historyNotificationAtomFamily(userData.userId), 'match_7s');
    }
    // 1회 노출 이후 red dot 노출 정책 = Quality 매치 이상
    else if (viewCount >= 1 && matchDurationMs >= QUALITY_MATCH_DURATION_MS) {
      set(historyNotificationAtomFamily(userData.userId), 'match_120s');
    }
  }
);
